import React, { useEffect, useState } from "react";
import { X, Save, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

/**
 * iter-169k — Septic design side-sheet.
 *
 * Right-side slide-over storing per-project septic design specs
 * (bedrooms, daily flow, tank size, tile bed area, soil perc rate,
 * free-form notes). Persists via `PATCH /api/projects/:id` with a
 * `septic_design` sub-doc. Schedule 3 pulls these via
 * `_values_for` so any subsequent AcroForm mapping can auto-fill.
 */
const FIELDS = [
    { key: "bedrooms", label: "Bedrooms", placeholder: "e.g. 3", type: "number" },
    { key: "daily_flow_l", label: "Total daily design flow (L/day)", placeholder: "e.g. 1600", type: "number" },
    { key: "tank_size_l", label: "Septic tank size (L)", placeholder: "e.g. 3600", type: "number" },
    { key: "tile_bed_area_m2", label: "Tile bed area (m²)", placeholder: "e.g. 60", type: "number" },
    { key: "soil_perc_min", label: "Soil percolation rate (min/cm)", placeholder: "e.g. 10", type: "number" },
    { key: "loading_rate", label: "Loading rate (L/m²·day)", placeholder: "e.g. 30", type: "number" },
];

export default function SepticDesignSheet({ open, onOpenChange, project, onSaved }) {
    const [values, setValues] = useState(() => ({ ...(project?.septic_design || {}) }));
    const [saving, setSaving] = useState(false);
    const [notes, setNotes] = useState(project?.septic_design?.notes || "");

    useEffect(() => {
        if (open && project) {
            setValues({ ...(project.septic_design || {}) });
            setNotes(project.septic_design?.notes || "");
        }
    }, [open, project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const setField = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                septic_design: { ...values, notes: notes.trim() },
            };
            await onSaved(payload);
            onOpenChange(false);
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;
    return (
        <div
            data-testid="septic-design-sheet"
            className="fixed inset-0 z-[110] flex justify-end"
            onClick={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}
        >
            <div className="absolute inset-0 bg-black/60" />
            {/* iter-175 — sheet body uses a flex column so header +
                Save row are pinned and the middle scrolls only if
                really needed. Numeric inputs are packed into a 2-col
                grid and paddings tightened so the whole form fits a
                phone-portrait viewport without scrolling. */}
            <div className="relative bg-[#0A1128] border-l border-[#2A3655] w-full max-w-md h-full shadow-2xl flex flex-col">
                <div className="bg-[#0A1128] border-b border-[#2A3655] px-4 py-2.5 flex items-center justify-between shrink-0">
                    <div>
                        <div className="text-sm font-bold text-white">Septic design</div>
                        <div className="text-[11px] text-slate-400 leading-tight">Feeds Schedule 3 auto-fill for this project.</div>
                    </div>
                    <button
                        data-testid="btn-close-septic-sheet"
                        onClick={() => onOpenChange(false)}
                        className="p-1.5 rounded hover:bg-[#101a35]"
                    >
                        <X className="w-4 h-4 text-slate-300" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        {FIELDS.map((f) => (
                            <div key={f.key} className="space-y-1 min-w-0">
                                <Label htmlFor={`septic-${f.key}`} className="text-slate-300 text-[10px] uppercase tracking-wider leading-tight block">
                                    {f.label}
                                </Label>
                                <Input
                                    id={`septic-${f.key}`}
                                    data-testid={`input-septic-${f.key}`}
                                    type={f.type}
                                    inputMode="decimal"
                                    placeholder={f.placeholder}
                                    value={values[f.key] ?? ""}
                                    onChange={(e) => setField(f.key, e.target.value)}
                                    className="bg-[#101a35] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF] h-9 text-sm"
                                />
                            </div>
                        ))}
                    </div>

                    <div className="space-y-1">
                        <Label htmlFor="septic-notes" className="text-slate-300 text-[10px] uppercase tracking-wider">
                            Notes
                        </Label>
                        <textarea
                            id="septic-notes"
                            data-testid="input-septic-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            placeholder="Soil profile, treatment type, pump specifics…"
                            className="w-full bg-[#101a35] border border-[#2A3655] rounded-md px-2 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
                        />
                    </div>
                </div>

                <div className="border-t border-[#2A3655] bg-[#0A1128] px-4 py-2.5 flex items-center justify-end shrink-0">
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="btn-save-septic-design"
                        className="bg-[#007AFF] hover:bg-[#0066CC] text-white h-9"
                    >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
}
